import React from 'react';
import { UseFormReturn, Controller } from 'react-hook-form';

import { Company, ConstantArray, Paths, PathValue } from '_lib/api/apiTypes.shim';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { FlexCard } from '_lib/components/FlexCard/FlexCard';
import { InputAdornment } from '_lib/components/InputAdornment/InputAdornment';
import { Stack } from '_lib/components/Stack/Stack';
import { TextField } from '_lib/components/TextField/TextField';

export const companyConfigBillingFormValues = [
  'callCenter.billing.perCall',
  'callCenter.billing.perAppointment',
  'callCenter.billing.perCancel',
  'callCenter.billing.discount',
  'callCenter.billing.weeklyIntegrationFee',
  'callCenter.billing.minimumWeeklyServiceFee'
] satisfies ConstantArray<Paths<Company>>;

export type CompanyConfigBillingFormValue = (typeof companyConfigBillingFormValues)[number];

export type CompanyConfigBillingFormValues = {
  [K in CompanyConfigBillingFormValue]: PathValue<Company, K>;
};

/**
 * Form is only responsible for fields it controls, and we can check for the generic constraint in the
 * props in order for this to be reusable.
 */
type CompanyConfigBillingFormProps<T extends CompanyConfigBillingFormValues> = {
  companyConfigBillingFormContext: T extends CompanyConfigBillingFormValues ? UseFormReturn<T> : never;
  disabled?: boolean;
};

export const CompanyConfigBillingForm = <T extends CompanyConfigBillingFormValues>(
  props: CompanyConfigBillingFormProps<T>
) => {
  const { companyConfigBillingFormContext, disabled } = props;
  const { control } = companyConfigBillingFormContext;

  return (
    <Stack useFlexGap spacing={2} sx={{ width: '100%' }}>
      <FlexCard>
        <PrimaryCardHeader title='Billing Configuration' />
        <Stack useFlexGap sx={{ padding: 2, width: '100%' }} spacing={2}>
          <FlexBox sx={{ gap: 2, width: '100%', flexDirection: { xs: 'column', md: 'row' } }}>
            <Stack useFlexGap sx={{ flex: 1 }}>
              <Controller
                name='callCenter.billing.perCall'
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value === undefined || field.value === null ? '' : (field.value as number).toString()}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : parseFloat(e.target.value);
                      field.onChange(value);
                    }}
                    label='Price Per Call'
                    type='number'
                    slotProps={{
                      input: {
                        inputProps: {
                          step: 0.01,
                          min: 0
                        },
                        startAdornment: <InputAdornment position='start'>$</InputAdornment>
                      }
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    disabled={disabled}
                  />
                )}
              />
              <Controller
                name='callCenter.billing.perAppointment'
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value === undefined || field.value === null ? '' : (field.value as number).toString()}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : parseFloat(e.target.value);
                      field.onChange(value);
                    }}
                    label='Price Per Appointment'
                    type='number'
                    slotProps={{
                      input: {
                        inputProps: {
                          step: 0.01,
                          min: 0
                        },
                        startAdornment: <InputAdornment position='start'>$</InputAdornment>
                      }
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    disabled={disabled}
                  />
                )}
              />
              <Controller
                name='callCenter.billing.perCancel'
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value === undefined || field.value === null ? '' : (field.value as number).toString()}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : parseFloat(e.target.value);
                      field.onChange(value);
                    }}
                    label='Price Per Cancel'
                    type='number'
                    slotProps={{
                      input: {
                        inputProps: {
                          step: 0.01,
                          min: 0
                        },
                        startAdornment: <InputAdornment position='start'>$</InputAdornment>
                      }
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    disabled={disabled}
                  />
                )}
              />
            </Stack>
            <Stack useFlexGap sx={{ flex: 1 }}>
              <Controller
                name='callCenter.billing.discount'
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value === undefined || field.value === null ? '' : (field.value as number).toString()}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : parseFloat(e.target.value);
                      field.onChange(value);
                    }}
                    label='Discount Percent'
                    type='number'
                    slotProps={{
                      input: {
                        inputProps: {
                          step: 1,
                          min: 0,
                          max: 100
                        },
                        startAdornment: <InputAdornment position='start'>%</InputAdornment>
                      }
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    disabled={disabled}
                  />
                )}
              />
              <Controller
                name='callCenter.billing.weeklyIntegrationFee'
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value === undefined || field.value === null ? '' : (field.value as number).toString()}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : parseFloat(e.target.value);
                      field.onChange(value);
                    }}
                    label='Weekly Integration Fee'
                    type='number'
                    slotProps={{
                      input: {
                        inputProps: {
                          step: 0.01,
                          min: 0
                        },
                        startAdornment: <InputAdornment position='start'>$</InputAdornment>
                      }
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    disabled={disabled}
                  />
                )}
              />
              <Controller
                name='callCenter.billing.minimumWeeklyServiceFee'
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    value={field.value === undefined || field.value === null ? '' : (field.value as number).toString()}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : parseFloat(e.target.value);
                      field.onChange(value);
                    }}
                    label='Minimum Weekly Service Fee'
                    type='number'
                    slotProps={{
                      input: {
                        inputProps: {
                          step: 0.01,
                          min: 0
                        },
                        startAdornment: <InputAdornment position='start'>$</InputAdornment>
                      }
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    disabled={disabled}
                  />
                )}
              />
            </Stack>
          </FlexBox>
        </Stack>
      </FlexCard>
    </Stack>
  );
};
